import { createResponsiveTextStroke } from '@/styles/textStroke';
import coinIcon from '@/assets/coin.webp';
import type { RankCardBackground } from './rankCardPresets';

export interface RankCardProps {
  rank: number;
  username: string;
  score: number;
  background: RankCardBackground;
  isHighlighted?: boolean;
  className?: string;
  /** Show the score + coin on the right. Set false for contexts where the value
   *  is not a points total (e.g. a personal daily standing card). Default true. */
  showScore?: boolean;
  /** When set, the score is shown with this text label instead of the coin icon
   *  (e.g. "matchs"), for non-points values. */
  scoreLabel?: string;
}

export function RankCard({
  rank,
  username,
  score,
  background,
  isHighlighted = false,
  className = '',
  showScore = true,
  scoreLabel
}: RankCardProps) {
  const isGradient = background.type === 'gradient';
  const isDarkText = !isGradient;

  const backgroundStyle =
    background.type === 'gradient'
      ? {
          background: `linear-gradient(to right, ${background.color}, ${background.colorEnd ?? ''})`
        }
      : { backgroundColor: background.color };

  const textColor = isDarkText ? '#1A3A5C' : '#FFFFFF';

  return (
    <div
      className={`flex items-center rounded-xl px-3 py-2 ${isHighlighted ? 'ring-2 ring-white ring-offset-1 ring-offset-transparent' : ''} ${className}`}
      style={{
        ...backgroundStyle,
        border: '2px solid black',
        containerType: 'inline-size'
      }}
    >
      {/* Rank */}
      <span
        className="font-mind-boggle leading-none shrink-0"
        style={{
          fontSize: 'clamp(0.875rem, 6cqi, 1.5rem)',
          color: textColor,
          width: 'clamp(1.5rem, 10cqi, 2.5rem)',
          textAlign: 'center',
          ...(isGradient ? createResponsiveTextStroke(1, 0.05) : {})
        }}
      >
        #{rank}
      </span>

      {/* Username */}
      <span
        className="font-mind-boggle leading-none flex-1 text-center truncate mx-2"
        style={{
          fontSize: 'clamp(0.75rem, 5cqi, 1.25rem)',
          color: textColor,
          ...(isGradient ? createResponsiveTextStroke(1, 0.05) : {})
        }}
      >
        {username}
      </span>

      {/* Score + Coin */}
      {showScore && (
        <span className="flex items-center gap-1 shrink-0">
          <span
            className="font-mind-boggle leading-none"
            style={{
              fontSize: 'clamp(0.75rem, 5cqi, 1.25rem)',
              color: textColor,
              ...(isGradient ? createResponsiveTextStroke(1, 0.05) : {})
            }}
          >
            {score}
          </span>
          {scoreLabel ? (
            <span
              className="font-mind-boggle leading-none"
              style={{
                fontSize: 'clamp(0.625rem, 4cqi, 1rem)',
                color: textColor,
                opacity: 0.6,
                ...(isGradient ? createResponsiveTextStroke(1, 0.05) : {})
              }}
            >
              {scoreLabel}
            </span>
          ) : (
            <img
              src={coinIcon}
              alt="points"
              className="shrink-0"
              style={{
                width: 'clamp(14px, 5cqi, 22px)',
                height: 'clamp(14px, 5cqi, 22px)'
              }}
            />
          )}
        </span>
      )}
    </div>
  );
}

export default RankCard;
